import React from 'react'
import { Globe, KeyRound, Palette, Sparkles, Users } from 'lucide-react'
import { Card } from '@rallyshift/design-system'

type ReleaseKind = 'feature' | 'improvement' | 'fix'

interface Release {
  id: string
  /** Month granularity: the page is a what's-new, not an audit trail. */
  month: string
  kind: ReleaseKind
  icon: React.ComponentType<{ className?: string }>
  title: string
  summary: string
  points: string[]
}

/** Newest first. Written for organisers: what changed for them, not how it was built. */
const RELEASES: Release[] = [
  {
    id: 'volunteer-check-in-toggle',
    month: '2026-09',
    kind: 'improvement',
    icon: Sparkles,
    title: 'Zelf inchecken aan of uit',
    summary:
      'Je kiest nu zelf of vrijwilligers op de dag inchecken met hun telefoon, of dat jij de aanwezigheid bijhoudt.',
    points: [
      'Zet het aan of uit onder Organisatie → Op de dag zelf.',
      'Staat het uit, dan verdwijnt de pagina Inchecken uit het menu van vrijwilligers.',
      'Via Evenementdag registreer je aanwezigheid altijd zelf, wat de instelling ook is.',
    ],
  },
  {
    id: 'custom-domain',
    month: '2026-09',
    kind: 'feature',
    icon: Globe,
    title: 'Je portaal op je eigen webadres',
    summary:
      'Naast jouwevenement.rallyshift.io kan het portaal nu ook draaien op een adres van jezelf, zoals vrijwilligers.jouwevenement.nl.',
    points: [
      'Vraag het aan bij RallyShift; wij regelen het beveiligde certificaat.',
      'Uitnodigingen en e-mails linken daarna naar je eigen adres.',
      'Het rallyshift.io-adres blijft gewoon werken.',
    ],
  },
  {
    id: 'account-management',
    month: '2026-09',
    kind: 'feature',
    icon: KeyRound,
    title: 'Accounts van vrijwilligers beheren',
    summary:
      'Op de pagina Vrijwilligers stuur je iemand een nieuwe wachtwoordlink of haal je iemand van je lijst.',
    points: [
      'Wachtwoord vergeten? Eén klik stuurt een link die een uur geldig is.',
      'Selecteer meerdere vrijwilligers om ze in één keer een link te sturen of te verwijderen.',
      'Verwijderen haalt iemand alleen uit jouw organisatie; hun account blijft bestaan.',
    ],
  },
  {
    id: 'welcome-rallyshift',
    month: '2026-09',
    kind: 'feature',
    icon: Users,
    title: 'Welkom bij RallyShift',
    summary:
      'Het vrijwilligersportaal heet nu RallyShift. Je diensten, vrijwilligers en aanmeldingen zijn meegekomen.',
    points: [
      'Je organisatie heeft een eigen webadres: jouwevenement.rallyshift.io.',
      'Nodig vrijwilligers uit per e-mail, of laat iedereen zich aanmelden en keur ze goed.',
      'Bestaande vrijwilligers stellen bij hun eerste bezoek een nieuw wachtwoord in.',
    ],
  },
  {
    id: 'branding',
    month: '2026-09',
    kind: 'feature',
    icon: Palette,
    title: 'Jouw logo en kleur',
    summary:
      'Vrijwilligers zien jouw huisstijl, niet die van ons. Stel je logo en kleur in onder Organisatie.',
    points: [
      'Een live voorbeeld laat zien hoe de aanmeldpagina eruitziet.',
      'RallyShift bewaakt de leesbaarheid: tekst op knoppen blijft altijd goed te lezen.',
    ],
  },
]

const KIND_LABEL: Record<ReleaseKind, string> = {
  feature: 'Nieuw',
  improvement: 'Verbeterd',
  fix: 'Opgelost',
}

const KIND_STYLE: Record<ReleaseKind, string> = {
  feature: 'bg-action-primary text-text-on-primary',
  improvement: 'bg-status-info-bg text-status-info-text',
  fix: 'bg-status-success-bg text-status-success-text',
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number)
  return new Date(year, m - 1, 1).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })
}

/** Releases grouped by month, keeping the order they are listed in. */
function byMonth(releases: Release[]): [string, Release[]][] {
  const groups: [string, Release[]][] = []
  releases.forEach((release) => {
    const last = groups[groups.length - 1]
    if (last && last[0] === release.month) last[1].push(release)
    else groups.push([release.month, [release]])
  })
  return groups
}

/**
 * What's new in RallyShift. This page is ours rather than the organisation's, so it
 * uses the RallyShift clay throughout instead of the tenant brand around it.
 */
const SystemUpdatesPage: React.FC = () => {
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <header className="overflow-hidden rounded-lg bg-action-primary px-6 py-8 text-text-on-primary shadow-card sm:px-8">
        <p className="text-sm font-semibold uppercase tracking-wide opacity-80">RallyShift</p>
        <h1 className="mt-1 font-display text-display-lg">Wat is er nieuw</h1>
        <p className="mt-2 max-w-xl text-md opacity-90">
          Verbeteringen aan het platform waarop je jouw vrijwilligers organiseert. Nieuwe
          functies staan automatisch voor je klaar.
        </p>
      </header>

      {byMonth(RELEASES).map(([month, releases]) => (
        <section key={month} aria-labelledby={`month-${month}`}>
          <h2
            id={`month-${month}`}
            className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted"
          >
            {monthLabel(month)}
          </h2>
          <ol className="m-0 list-none space-y-4 p-0">
            {releases.map((release) => (
              <li key={release.id}>
                <Card>
                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-action-primary">
                      <release.icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="m-0 font-display text-display-sm text-text-primary">
                          {release.title}
                        </h3>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${KIND_STYLE[release.kind]}`}
                        >
                          {KIND_LABEL[release.kind]}
                        </span>
                      </div>
                      <p className="mt-1 text-md text-text-secondary">{release.summary}</p>
                      <ul className="mt-3 space-y-1.5 pl-0">
                        {release.points.map((point) => (
                          <li key={point} className="flex gap-2 text-sm text-text-primary">
                            <span
                              aria-hidden
                              className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-action-primary"
                            />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ol>
        </section>
      ))}

      <p className="text-center text-xs text-text-muted">
        Alleen zichtbaar voor beheerders · mogelijk gemaakt door{' '}
        <span className="font-bold text-action-primary">RallyShift</span>
      </p>
    </div>
  )
}

export default SystemUpdatesPage
